import { Locales } from '@/utils/language'
import { isGlassisMainEventPeriod, isKoreaTourismCampaignPeriod } from '@/features/promotion/constants'

import type { BannerImage } from '@/components/atomic/molecules/ImageBanner'

export interface BannerImageData {
  url?: string
  isOpenInNewWindow?: boolean
  tag?: string
  startDate?: string
  endDate?: string
}

const jp: BannerImage<BannerImageData>[] = [
  {
    src: '/images/banners/main_banner_3500_point_jp.webp',
    alt: 'Gangnamunni main banner',
    data: {
      isOpenInNewWindow: true,
      tag: 'jp_download',
    },
  },
  {
    type: 'video',
    src: '/images/offline-consultation/offline-consultation-banner.mp4',
    previewImageSrc: '/images/offline-consultation/offline-consultation-banner-preview.webp',
    alt: 'Seminar banner in tokyo',
    data: {
      isOpenInNewWindow: false,
      url: '/jp/info/offline-consultation',
    },
  },
  ...(isKoreaTourismCampaignPeriod()
    ? [
        {
          src: '/images/banners/main_banner_korea_tourism_campaign.png',
          alt: 'korea tourism campaign',
          data: {
            url: '/jp/posts/685923cc9ec3166c58cf6af2',
            tag: 'korea_tourism_campaign',
          },
        },
      ]
    : []),
  {
    src: '/images/banners/main_banner_column_jp_24_01_29.png',
    alt: 'Gangnamunni main banner',
    data: {
      url: '/jp/posts',
      tag: 'column_jp',
    },
  },
]

const kr: BannerImage<BannerImageData>[] = [
  ...(isGlassisMainEventPeriod()
    ? [
        {
          src: '/images/banners/glassis_banner_main.gif',
          alt: 'Gangnamunni glassis banner',
          data: {
            isOpenInNewWindow: true,
            url: 'https://www.gangnamunni.com/glassis',
            tag: 'glassis main',
          },
        },
      ]
    : []),
  {
    src: '/images/banners/main_banner_select_skinbooster_24_01_31.png',
    alt: 'Gangnamunni select skinbooster banner',
    data: {
      isOpenInNewWindow: true,
      url: 'https://www.gangnamunni.com/select/SkinBooster?utm_source=select_cheetah&utm_medium=homebanner&utm_campaign=select_Cheetah&utm_content=치타_스킨부스터',
      tag: 'select skinbooster',
    },
  },
  {
    src: '/images/banners/main_banner_unni_tip_24_01_29.png',
    alt: '언니네 꿀팁',
    data: {
      url: '/community/medical-tips',
      tag: 'medical_tip',
    },
  },
  {
    src: '/images/banners/main_banner_point_24_01_29.png',
    alt: 'Gangnamunni main banner',
    data: {
      isOpenInNewWindow: true,
      tag: 'point',
    },
  },
  {
    src: '/images/banners/main_banner_review_24_01_29.png',
    alt: '올바른 시술후기 작성법',
    data: {
      url: '/info/review-guide',
      tag: 'review_guide',
    },
  },
  {
    src: '/images/banners/main_banner_column_24_01_29.png',
    alt: 'Gangnamunni main banner',
    data: {
      url: '/posts',
      tag: 'column_kr',
    },
  },
]

const us: BannerImage<BannerImageData>[] = [
  {
    src: '/images/banners/us/global-write-review.gif',
    alt: 'Write review',
    data: {
      url: 'https://unnireview.oopy.io/?t=1714466436165',
      tag: 'global_write_review',
    },
  },
  {
    src: '/images/banners/us/global-interpretation.png',
    alt: 'Interpretation',
    data: {
      url: 'https://abr.ge/@gangnamunni/cheetah_us?deeplink_url=hbeauty%3A%2F%2Fpost%2F395',
      tag: 'global_interpretation',
    },
  },
  {
    src: '/images/banners/us/global-travel-guide.gif',
    alt: 'Travel guide',
    data: {
      url: 'https://abr.ge/@gangnamunni/cheetah_us?deeplink_url=hbeauty%3A%2F%2Fpost%2F411',
      tag: 'global_travel_guide',
    },
  },
  {
    src: '/images/banners/us/global-how-to-use.png',
    alt: 'How to use',
    data: {
      url: 'https://abr.ge/@gangnamunni/cheetah_us?deeplink_url=hbeauty%3A%2F%2Fpost%2F362',
      tag: 'global_how_to_use',
    },
  },
]

const th: BannerImage<BannerImageData>[] = [
  {
    src: '/images/features/home-banner/th/1_th_how_to_use_unni.gif',
    alt: 'how_to_use_unni',
    data: {
      url: 'https://abr.ge/@gangnamunni/cheetah_us?deeplink_url=hbeauty%3A%2F%2Fpost%2F374',
      tag: 'how_to_use_unni',
    },
  },
  {
    src: '/images/features/home-banner/th/2_th_post.gif',
    alt: 'post',
    data: {
      url: 'https://abr.ge/@gangnamunni/cheetah_us?deeplink_url=hbeauty%3A%2F%2Fpost%2F437',
      tag: 'post',
    },
  },
  {
    src: '/images/features/home-banner/th/3_th_concierge.gif',
    alt: 'concierge',
    data: {
      url: 'https://abr.ge/@gangnamunni/cheetah_us?deeplink_url=hbeauty%3A%2F%2Fpost%2F387',
      tag: 'concierge',
    },
  },
  {
    src: '/images/features/home-banner/th/4_th_review.gif',
    alt: 'review',
    data: {
      url: 'https://abr.ge/@gangnamunni/cheetah_us?deeplink_url=hbeauty%3A%2F%2Fpost%2F445',
      tag: 'review',
    },
  },
]

const cn: BannerImage<BannerImageData>[] = []

const data = { jp, kr, us, th, cn }
export const getBannerImages = (locale: Locales) => {
  const map = {
    [Locales.JA_JP]: data.jp,
    [Locales.EN_US]: data.us,
    [Locales.KO_KR]: data.kr,
    [Locales.TH_TH]: data.th,
    [Locales.ZH_HANS_CN]: data.cn,
    [Locales.ZH_HANT_TW]: data.cn,
  }

  return map[locale] || data.kr
}
